import type { AnrufregelnConfig, AnrufregelnResponse } from "./types";
/**
 * HTTP client for the strawatar-api / dashboard-api.
 *
 * All endpoint paths live here so backend contract changes touch one module.
 * The case endpoints (/api/v1/cases/...) are defined ahead of the backend
 * landing (Phase 5a contract): GET returns a CaseQualification, PATCH
 * accepts edited draft fields plus status="human_reviewed".
 */
import type {
  AgentTestCase,
  AgentTestCaseInput,
  AgentTestCaseList,
  AgentTestImportResult,
  AgentTestResult,
  AgentTestRun,
  AnalyticsKpis,
  AuditEvent,
  CallbackRequest,
  CallSummary,
  CaseQualification,
  CaseQualificationPatch,
  ComplianceOverview,
  EmergencyScript,
  GapMetrics,
  GapStat,
  GapStatus,
  GapSuggestResult,
  GapVerifyResult,
  CallGrades,
  GradeIssue,
  GradeJob,
  GradeRunResult,
  GradeSummary,
  IntentCount,
  KnowledgeGap,
  KnowledgePublication,
  KnowledgePublicationDetail,
  KnowledgePublicationList,
  KnowledgePublicationSourceList,
  MachineCredential,
  MachineCredentialCreateInput,
  MachineCredentialIssued,
  LLMConnection,
  LLMConnectionInput,
  LLMConnectionsOverview,
  LLMProbeResult,
  LLMTestResult,
  OnboardingState,
  OnboardingStateUpdate,
  OperatorFeedbackInput,
  OperatorFeedbackResult,
  PiiVerificationResult,
  PimProbeResult,
  PimStatus,
  PipelineLatency,
  PlaygroundTurnResult,
  PolicyCheckRequest,
  PolicyCheckResult,
  PolicyOverrides,
  ProviderSummaryContact,
  ProviderSummaryDetail,
  ProviderEvaluationFailureCode,
  ProviderEvaluationOutcome,
  ProviderEvaluationSession,
  ProviderSummaryList,
  ProviderSummaryStats,
  ProviderSummaryStatus,
  ProductLookupResult,
  QueueStatus,
  ReactFlowDocument,
  TimeseriesBucket,
  Topic,
  TopicCreateInput,
  TopicUpdateInput,
  Turn,
  WorkflowDefinition,
  WorkflowSummary,
  AgentSettings,
  AgentSettingsUpdate,
  AnalyticsKpiTrend,
  CallbackCreateInput,
  CallbackIntakeDetail,
  CallbackUpdate,
  CallSearchParams,
  CallSearchResult,
  ConfigArea,
  ConfigVersionDetail,
  ConfigVersionList,
  ControlCommand,
  ControlResult,
  CostEstimate,
  ErasureResult,
  HealthDetail,
  IonosConfig,
  KnowledgeDocuments,
  KnowledgeSearchResult,
  PlaygroundHistoryItem,
  PlaygroundLink,
  SyncStatus,
  TurnFeedbackRating,
  Voice,
  AssistantChatRequest,
  AssistantChatResponse,
  BetriebStatusResponse,
  HeuteResponse,
  HeuteFrageRequest,
  HeuteFrageResponse,
  RueckrufActionResponse,
  RueckrufClaimRequest,
  RueckrufDetail,
  RueckrufKontakt,
  RueckrufListResponse,
  RueckrufReleaseRequest,
  RueckrufResultRequest,
  RueckrufUndoRequest,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  /** Parsed `detail` field of the JSON error body, when present. */
  readonly detail: unknown;
  /** Parsed `code` field: a machine-readable cause the UI may branch on. */
  readonly code: string | null;

  constructor(
    status: number,
    message: string,
    detail: unknown = null,
    code: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.code = code;
  }
}

export class ApiClient {
  private readonly getHeaders: () => Promise<Record<string, string>>;
  private readonly base: string;

  constructor(getHeaders: () => Promise<Record<string, string>>, base = "") {
    this.getHeaders = getHeaders;
    this.base = base;
  }

  private async send(path: string, init?: RequestInit): Promise<Response> {
    const response = await fetch(`${this.base}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(await this.getHeaders()),
        ...(init?.headers ?? {}),
      },
    });
    if (!response.ok) {
      let detail: unknown = null;
      let code: string | null = null;
      try {
        const body = (await response.json()) as { detail?: unknown; code?: unknown };
        detail = body.detail;
        code = typeof body.code === "string" ? body.code : null;
      } catch {
        // Non-JSON error body -- keep detail and code null.
      }
      throw new ApiError(
        response.status,
        `${path}: HTTP ${response.status}`,
        detail,
        code,
      );
    }
    return response;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await this.send(path, init);
    return (await response.json()) as T;
  }

  /** For endpoints returning 204 No Content. */
  private async requestVoid(path: string, init?: RequestInit): Promise<void> {
    await this.send(path, init);
  }

  private jsonInit(method: string, body: unknown): RequestInit {
    return {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };
  }

  async listActiveCalls(): Promise<CallSummary[]> {
    const data = await this.request<{ active_calls: CallSummary[] }>(
      "/api/v1/calls/active/list",
    );
    return data.active_calls;
  }

  /** Recent calls, newest first (fragobert.calls, started_at DESC). */
  async listCalls(limit = 50): Promise<CallSummary[]> {
    const data = await this.request<{ calls: CallSummary[] }>(
      `/api/v1/calls?limit=${limit}`,
    );
    return data.calls;
  }

  async getCall(callId: string): Promise<CallSummary> {
    return this.request<CallSummary>(
      `/api/v1/calls/${encodeURIComponent(callId)}`,
    );
  }

  async getCallTurns(callId: string): Promise<Turn[]> {
    const data = await this.request<{ turns: Turn[] }>(
      `/api/v1/calls/${encodeURIComponent(callId)}/turns`,
    );
    return data.turns;
  }

  /* ----------------------------------------- machine credentials */

  async listMachineCredentials(): Promise<MachineCredential[]> {
    return this.request<MachineCredential[]>("/api/v1/machine-credentials");
  }

  async createMachineCredential(
    input: MachineCredentialCreateInput,
  ): Promise<MachineCredentialIssued> {
    return this.request<MachineCredentialIssued>(
      "/api/v1/machine-credentials",
      this.jsonInit("POST", input),
    );
  }

  async revokeMachineCredential(credentialId: string): Promise<MachineCredential> {
    return this.request<MachineCredential>(
      `/api/v1/machine-credentials/${encodeURIComponent(credentialId)}/revoke`,
      { method: "POST" },
    );
  }

  /** List CaseQualification drafts. Backend lands after Phase 5a. */
  async listCases(): Promise<CaseQualification[]> {
    const data = await this.request<{ cases: CaseQualification[] }>(
      "/api/v1/cases",
    );
    return data.cases;
  }

  async getCase(callId: string): Promise<CaseQualification> {
    return this.request<CaseQualification>(
      `/api/v1/cases/${encodeURIComponent(callId)}`,
    );
  }

  /**
   * Persist operator edits and mark the case as human_reviewed
   * (AI Act Art. 22: bot prepares, human decides).
   */
  async reviewCase(
    callId: string,
    patch: CaseQualificationPatch,
  ): Promise<CaseQualification> {
    return this.request<CaseQualification>(
      `/api/v1/cases/${encodeURIComponent(callId)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...patch, status: "human_reviewed" }),
      },
    );
  }

  /* ------------------------------------------------------ analytics */

  async getAnalyticsKpis(period = "7d"): Promise<AnalyticsKpis> {
    const data = await this.request<{ kpis: AnalyticsKpis }>(
      `/api/v1/analytics/kpis?period=${encodeURIComponent(period)}`,
    );
    return data.kpis;
  }

  async getAnalyticsTimeseries(period = "7d"): Promise<TimeseriesBucket[]> {
    const data = await this.request<{ buckets: TimeseriesBucket[] }>(
      `/api/v1/analytics/timeseries?period=${encodeURIComponent(period)}`,
    );
    return data.buckets;
  }

  async getIntentDistribution(period = "7d"): Promise<IntentCount[]> {
    const data = await this.request<{ intents: IntentCount[] }>(
      `/api/v1/analytics/intents?period=${encodeURIComponent(period)}`,
    );
    return data.intents;
  }

  /** Business categorisation of calls (CaseIntent), not raw turn intents. */
  async getCaseIntentDistribution(period = "7d"): Promise<IntentCount[]> {
    const data = await this.request<{ case_intents: IntentCount[] }>(
      `/api/v1/analytics/case-intents?period=${encodeURIComponent(period)}`,
    );
    return data.case_intents;
  }

  /** Per-stage pipeline latency percentiles (p50/p95/p99). */
  async getPipelineLatency(period = "7d"): Promise<PipelineLatency> {
    return this.request<PipelineLatency>(
      `/api/v1/telemetry/pipeline?period=${encodeURIComponent(period)}`,
    );
  }

  async getKnowledgeGapStats(period = "7d"): Promise<GapStat[]> {
    const data = await this.request<{ gaps: GapStat[] }>(
      `/api/v1/analytics/knowledge-gap-stats?period=${encodeURIComponent(period)}`,
    );
    return data.gaps;
  }

  /* -------------------------------------------------- answer grades */

  async getGradeSummary(period: "7d" | "30d" | "all" = "all"): Promise<GradeSummary> {
    return this.request<GradeSummary>(
      `/api/v1/grades/summary?period=${encodeURIComponent(period)}`,
    );
  }

  async listGradeIssues(
    limit = 25,
    period: "7d" | "30d" | "all" = "all",
    callId?: string,
    category?: string,
    severity?: string,
  ): Promise<GradeIssue[]> {
    const params = new URLSearchParams({ limit: String(limit), period });
    if (callId) params.set("call_id", callId);
    if (category) params.set("category", category);
    if (severity) params.set("severity", severity);
    return this.request<GradeIssue[]>(`/api/v1/grades/issues?${params.toString()}`);
  }

  async getCallGrades(callId: string): Promise<CallGrades> {
    return this.request<CallGrades>(`/api/v1/grades/calls/${encodeURIComponent(callId)}`);
  }

  async runCallGrading(callId: string, regrade = false): Promise<GradeRunResult> {
    const suffix = regrade ? "?regrade=true" : "";
    return this.request<GradeRunResult>(
      `/api/v1/grades/calls/${encodeURIComponent(callId)}/run${suffix}`,
      { method: "POST" },
    );
  }

  async runPeriodGrading(period: "7d" | "30d" | "all"): Promise<{ job_id: string; status: string }> {
    return this.request<{ job_id: string; status: string }>(
      "/api/v1/grades/run",
      this.jsonInit("POST", { period }),
    );
  }

  async getGradeJob(jobId: string): Promise<GradeJob> {
    return this.request<GradeJob>(`/api/v1/grades/run/${encodeURIComponent(jobId)}`);
  }

  async getTranscriptTemplate(): Promise<string> {
    const data = await this.request<{ template: string }>("/api/v1/grades/transcripts/template");
    return data.template;
  }

  async gradeTranscript(body: {
    transcript: string;
    note?: string;
    started_at?: string;
  }): Promise<GradeRunResult> {
    return this.request<GradeRunResult>("/api/v1/grades/transcripts", this.jsonInit("POST", body));
  }

  /* ------------------------------------------ provider email summaries */

  async listProviderSummaries(options: {
    status?: ProviderSummaryStatus;
    q?: string;
    abteilung?: string;
    ergebnis?: import("./types").ProviderSummaryErgebnis;
    from?: string;
    to?: string;
    cursor?: string;
    limit?: number;
  } = {}): Promise<ProviderSummaryList> {
    const query = new URLSearchParams({ limit: String(options.limit ?? 50) });
    if (options.status) query.set("status", options.status);
    if (options.q) query.set("q", options.q);
    if (options.abteilung) query.set("abteilung", options.abteilung);
    if (options.ergebnis) query.set("ergebnis", options.ergebnis);
    if (options.from) query.set("from", options.from);
    if (options.to) query.set("to", options.to);
    if (options.cursor) query.set("cursor", options.cursor);
    return this.request<ProviderSummaryList>(`/api/v1/provider-summaries?${query}`);
  }

  async getProviderSummaryStats(options: {
    from?: string;
    to?: string;
  } = {}): Promise<ProviderSummaryStats> {
    const query = new URLSearchParams();
    if (options.from) query.set("from", options.from);
    if (options.to) query.set("to", options.to);
    const suffix = query.size > 0 ? `?${query}` : "";
    return this.request<ProviderSummaryStats>(
      `/api/v1/provider-summaries/stats${suffix}`,
    );
  }

  async getProviderSummary(summaryId: string): Promise<ProviderSummaryDetail> {
    return this.request<ProviderSummaryDetail>(
      `/api/v1/provider-summaries/${encodeURIComponent(summaryId)}`,
    );
  }

  async revealProviderSummaryContact(
    summaryId: string,
  ): Promise<ProviderSummaryContact> {
    return this.request<ProviderSummaryContact>(
      `/api/v1/summaries/${encodeURIComponent(summaryId)}/contact`,
    );
  }

  /* ------------------------------------------------------- Auswertung */

  async startAuswertung(body: import("./types").AuswertungStartRequest): Promise<import("./types").AuswertungStartResponse> {
    return this.request("/api/v1/auswertung/laeufe", this.jsonInit("POST", body));
  }

  async listAuswertung(): Promise<import("./types").AuswertungList> {
    return this.request("/api/v1/auswertung/laeufe");
  }

  async getAuswertung(id: string): Promise<import("./types").AuswertungRun> {
    return this.request(`/api/v1/auswertung/laeufe/${encodeURIComponent(id)}`);
  }

  /* ------------------------------------------------------- Leitstand */

  async listRueckrufe(options: {
    status?: "offen" | "erledigt" | "alle";
    abteilung?: string;
  } = {}): Promise<RueckrufListResponse> {
    const query = new URLSearchParams({ status: options.status ?? "offen" });
    if (options.abteilung) query.set("abteilung", options.abteilung);
    return this.request<RueckrufListResponse>(`/api/v1/rueckrufe?${query}`);
  }

  async getRueckruf(callbackId: string): Promise<RueckrufDetail> {
    return this.request<RueckrufDetail>(
      `/api/v1/rueckrufe/${encodeURIComponent(callbackId)}`,
    );
  }

  async getRueckrufKontakt(callbackId: string): Promise<RueckrufKontakt> {
    return this.request<RueckrufKontakt>(
      `/api/v1/rueckrufe/${encodeURIComponent(callbackId)}/kontakt`,
    );
  }

  async claimRueckruf(
    callbackId: string,
    body: RueckrufClaimRequest,
  ): Promise<RueckrufActionResponse> {
    return this.request<RueckrufActionResponse>(
      `/api/v1/rueckrufe/${encodeURIComponent(callbackId)}/uebernehmen`,
      this.jsonInit("POST", body),
    );
  }

  async releaseRueckruf(
    callbackId: string,
    body: RueckrufReleaseRequest,
  ): Promise<RueckrufActionResponse> {
    return this.request<RueckrufActionResponse>(
      `/api/v1/rueckrufe/${encodeURIComponent(callbackId)}/freigeben`,
      this.jsonInit("POST", body),
    );
  }

  async setRueckrufResult(
    callbackId: string,
    body: RueckrufResultRequest,
  ): Promise<RueckrufActionResponse> {
    return this.request<RueckrufActionResponse>(
      `/api/v1/rueckrufe/${encodeURIComponent(callbackId)}/ergebnis`,
      this.jsonInit("POST", body),
    );
  }

  async undoRueckrufAction(
    callbackId: string,
    body: RueckrufUndoRequest,
  ): Promise<RueckrufActionResponse> {
    return this.request<RueckrufActionResponse>(
      `/api/v1/rueckrufe/${encodeURIComponent(callbackId)}/rueckgaengig`,
      this.jsonInit("POST", body),
    );
  }

  async getBetriebStatus(): Promise<BetriebStatusResponse> {
    return this.request<BetriebStatusResponse>("/api/v1/betrieb/status");
  }

  async getHeute(): Promise<HeuteResponse> {
    return this.request<HeuteResponse>("/api/v1/heute");
  }

  async frageHeute(body: HeuteFrageRequest): Promise<HeuteFrageResponse> {
    return this.request<HeuteFrageResponse>(
      "/api/v1/heute/frage",
      this.jsonInit("POST", body),
    );
  }

  async getProviderEvaluation(): Promise<ProviderEvaluationSession | null> {
    return this.request<ProviderEvaluationSession | null>(
      "/api/v1/summaries/evaluation",
    );
  }

  async startProviderEvaluation(
    scenarioId = "manual-test-call",
  ): Promise<ProviderEvaluationSession> {
    return this.request<ProviderEvaluationSession>(
      "/api/v1/summaries/evaluation/start",
      this.jsonInit("POST", { scenario_id: scenarioId }),
    );
  }

  async stopProviderEvaluation(
    outcome: Exclude<ProviderEvaluationOutcome, "pending">,
    failureCodes: ProviderEvaluationFailureCode[] = [],
  ): Promise<ProviderEvaluationSession> {
    return this.request<ProviderEvaluationSession>(
      "/api/v1/summaries/evaluation/stop",
      this.jsonInit("POST", { outcome, failure_codes: failureCodes }),
    );
  }

  /* -------------------------------------------------- knowledge gaps */

  async listKnowledgeGaps(
    status: GapStatus,
  ): Promise<{ gaps: KnowledgeGap[]; total: number }> {
    return this.request<{ gaps: KnowledgeGap[]; total: number }>(
      `/api/v1/knowledge-gaps?status=${encodeURIComponent(status)}`,
    );
  }

  async getKnowledgeGapMetrics(): Promise<GapMetrics> {
    return this.request<GapMetrics>("/api/v1/knowledge-gaps/metrics");
  }

  async resolveKnowledgeGap(
    gapId: string,
    status: "resolved" | "dismissed" = "resolved",
  ): Promise<{ gap_id: string; status: string }> {
    return this.request(
      `/api/v1/knowledge-gaps/${encodeURIComponent(gapId)}/resolve`,
      this.jsonInit("POST", { status }),
    );
  }

  async reopenKnowledgeGap(
    gapId: string,
  ): Promise<{ gap_id: string; status: string }> {
    return this.request(
      `/api/v1/knowledge-gaps/${encodeURIComponent(gapId)}/reopen`,
      { method: "POST" },
    );
  }

  /** Operator correction of a gap's question/category and audience. */
  async updateKnowledgeGap(
    gapId: string,
    patch: { suggested_category?: string; suggested_audience?: string },
  ): Promise<KnowledgeGap> {
    return this.request(
      `/api/v1/knowledge-gaps/${encodeURIComponent(gapId)}`,
      this.jsonInit("PATCH", patch),
    );
  }

  async verifyKnowledgeGap(gapId: string): Promise<GapVerifyResult> {
    return this.request<GapVerifyResult>(
      `/api/v1/knowledge-gaps/${encodeURIComponent(gapId)}/verify`,
    );
  }

  async suggestKnowledgeGap(gapId: string): Promise<GapSuggestResult> {
    return this.request<GapSuggestResult>(
      `/api/v1/knowledge-gaps/${encodeURIComponent(gapId)}/suggest`,
    );
  }

  /* ------------------------------------------ knowledge publications */

  async listKnowledgePublications(): Promise<KnowledgePublicationList> {
    return this.request<KnowledgePublicationList>(
      "/api/v1/knowledge-publications",
    );
  }

  async listKnowledgePublicationSources(): Promise<KnowledgePublicationSourceList> {
    return this.request<KnowledgePublicationSourceList>(
      "/api/v1/knowledge-publications/sources",
    );
  }

  async getKnowledgePublication(
    candidateId: string,
  ): Promise<KnowledgePublicationDetail> {
    return this.request<KnowledgePublicationDetail>(
      `/api/v1/knowledge-publications/${encodeURIComponent(candidateId)}`,
    );
  }

  async proposeKnowledgePublication(pageId: string): Promise<KnowledgePublication> {
    return this.request<KnowledgePublication>(
      "/api/v1/knowledge-publications",
      this.jsonInit("POST", { page_id: pageId }),
    );
  }

  async approveKnowledgePublication(
    candidateId: string,
  ): Promise<KnowledgePublication> {
    return this.request<KnowledgePublication>(
      `/api/v1/knowledge-publications/${encodeURIComponent(candidateId)}/approve`,
      { method: "POST" },
    );
  }

  async stageKnowledgePublication(
    candidateId: string,
  ): Promise<KnowledgePublication> {
    return this.request<KnowledgePublication>(
      `/api/v1/knowledge-publications/${encodeURIComponent(candidateId)}/stage`,
      { method: "POST" },
    );
  }

  /* ------------------------------------------------ queue + callbacks */

  async getQueueStatus(): Promise<QueueStatus> {
    return this.request<QueueStatus>("/api/v1/queue/status");
  }

  async listCallbacks(
    status = "pending",
  ): Promise<{ callbacks: CallbackRequest[]; total: number }> {
    return this.request(
      `/api/v1/callbacks?status=${encodeURIComponent(status)}`,
    );
  }

  /* -------------------------------------------------------- workflows */

  async listWorkflows(): Promise<WorkflowSummary[]> {
    return this.request<WorkflowSummary[]>("/api/v1/workflows");
  }

  async getWorkflow(workflow: string): Promise<WorkflowDefinition> {
    return this.request<WorkflowDefinition>(
      `/api/v1/workflows/${encodeURIComponent(workflow)}`,
    );
  }

  async validateWorkflow(
    workflow: string,
    name: string,
    definition: ReactFlowDocument,
  ): Promise<{ errors: string[] }> {
    return this.request(
      `/api/v1/workflows/${encodeURIComponent(workflow)}/validate`,
      this.jsonInit("POST", { name, definition }),
    );
  }

  /** Creates a new version of the workflow; returns it incl. version. */
  async saveWorkflow(
    workflow: string,
    name: string,
    definition: ReactFlowDocument,
  ): Promise<WorkflowDefinition> {
    return this.request<WorkflowDefinition>(
      `/api/v1/workflows/${encodeURIComponent(workflow)}`,
      this.jsonInit("PUT", { name, definition }),
    );
  }

  /* -------------------------------------------------- LLM connections */

  async listLlmConnections(): Promise<LLMConnection[]> {
    return (await this.getLlmConnectionsOverview()).connections;
  }

  /** Connections plus the non-EU waiver status (System > Verbindungen). */
  async getLlmConnectionsOverview(): Promise<LLMConnectionsOverview> {
    const data = await this.request<Partial<LLMConnectionsOverview>>(
      "/api/v1/connections/llm",
    );
    return {
      connections: data.connections ?? [],
      non_eu_waiver: data.non_eu_waiver ?? false,
      openrouter_base_url: data.openrouter_base_url ?? "https://openrouter.ai/api/v1",
    };
  }

  async createLlmConnection(body: LLMConnectionInput): Promise<LLMConnection> {
    const data = await this.request<{ connection: LLMConnection }>(
      "/api/v1/connections/llm",
      this.jsonInit("POST", body),
    );
    return data.connection;
  }

  async updateLlmConnection(
    id: string,
    body: Partial<LLMConnectionInput>,
  ): Promise<LLMConnection> {
    const data = await this.request<{ connection: LLMConnection }>(
      `/api/v1/connections/llm/${encodeURIComponent(id)}`,
      this.jsonInit("PUT", body),
    );
    return data.connection;
  }

  async deleteLlmConnection(id: string): Promise<void> {
    await this.requestVoid(
      `/api/v1/connections/llm/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  }

  async testLlmConnection(id: string): Promise<LLMTestResult> {
    return this.request<LLMTestResult>(
      `/api/v1/connections/llm/${encodeURIComponent(id)}/test`,
      { method: "POST" },
    );
  }

  /* ------------------------------------------------------- compliance */

  async getComplianceOverview(): Promise<ComplianceOverview> {
    return this.request<ComplianceOverview>("/api/v1/compliance/overview");
  }

  async getAuditLog(
    limit = 50,
    offset = 0,
  ): Promise<{ events: AuditEvent[]; total: number }> {
    return this.request(
      `/api/v1/compliance/audit-log?limit=${limit}&offset=${offset}`,
    );
  }

  /* ----------------------------------------------------------- policy */

  async getPolicyOverrides(): Promise<PolicyOverrides> {
    const data = await this.request<{ overrides: PolicyOverrides }>(
      "/api/v1/policy/overrides",
    );
    return data.overrides;
  }

  async savePolicyOverrides(
    overrides: PolicyOverrides,
  ): Promise<{ status: string }> {
    return this.request(
      "/api/v1/policy/overrides",
      this.jsonInit("PUT", { overrides }),
    );
  }

  /* -------------------------------------------------------------- pim */

  async getPimStatus(): Promise<PimStatus> {
    return this.request<PimStatus>("/api/v1/pim/status");
  }

  async lookupPimProduct(articleNumber: string): Promise<ProductLookupResult> {
    return this.request<ProductLookupResult>(
      `/api/v1/pim/products/${encodeURIComponent(articleNumber)}`,
    );
  }

  async probePim(): Promise<PimProbeResult> {
    return this.request<PimProbeResult>("/api/v1/pim/probe", { method: "POST" });
  }

  /* ------------------------------------------------------------ topics */

  async listTopics(): Promise<Topic[]> {
    return this.request<Topic[]>("/api/v1/topics");
  }

  async createTopic(body: TopicCreateInput): Promise<Topic> {
    return this.request<Topic>("/api/v1/topics", this.jsonInit("POST", body));
  }

  async updateTopic(topicId: string, body: TopicUpdateInput): Promise<Topic> {
    return this.request<Topic>(
      `/api/v1/topics/${encodeURIComponent(topicId)}`,
      this.jsonInit("PUT", body),
    );
  }

  /* ------------------------------------------------ operator feedback */

  async submitOperatorFeedback(
    body: OperatorFeedbackInput,
  ): Promise<OperatorFeedbackResult> {
    return this.request<OperatorFeedbackResult>(
      "/api/v1/operator-feedback",
      this.jsonInit("POST", body),
    );
  }

  /* -------------------------------------------------------- onboarding */

  async getOnboardingState(): Promise<OnboardingState> {
    const data = await this.request<{ onboarding: OnboardingState }>(
      "/api/v1/onboarding",
    );
    return data.onboarding;
  }

  async updateOnboardingState(
    body: OnboardingStateUpdate,
  ): Promise<OnboardingState> {
    const data = await this.request<{ onboarding: OnboardingState }>(
      "/api/v1/onboarding",
      this.jsonInit("PUT", body),
    );
    return data.onboarding;
  }

  /* ------------------------------------------------------- playground */

  async playgroundTurn(
    userText: string,
    history: PlaygroundHistoryItem[] = [],
  ): Promise<PlaygroundTurnResult & { links?: PlaygroundLink[] }> {
    return this.request<PlaygroundTurnResult & { links?: PlaygroundLink[] }>(
      "/api/v1/playground/turn",
      this.jsonInit("POST", { user_text: userText, history }),
    );
  }

  /* ------------------------------------------ agent test suite */

  async listAgentTestCases(options: {
    q?: string;
    tag?: string;
    enabled?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<AgentTestCaseList> {
    const params = new URLSearchParams();
    if (options.q) params.set("q", options.q);
    if (options.tag) params.set("tag", options.tag);
    if (options.enabled !== undefined) params.set("enabled", String(options.enabled));
    params.set("limit", String(options.limit ?? 50));
    params.set("offset", String(options.offset ?? 0));
    return this.request<AgentTestCaseList>(`/api/v1/agent-tests/cases?${params.toString()}`);
  }

  async createAgentTestCase(input: AgentTestCaseInput): Promise<AgentTestCase> {
    return this.request<AgentTestCase>("/api/v1/agent-tests/cases", this.jsonInit("POST", input));
  }

  async updateAgentTestCase(caseId: string, input: AgentTestCaseInput): Promise<AgentTestCase> {
    return this.request<AgentTestCase>(
      `/api/v1/agent-tests/cases/${encodeURIComponent(caseId)}`,
      this.jsonInit("PUT", input),
    );
  }

  async importAgentTestCases(
    format: "csv" | "json",
    content: string,
    dryRun: boolean,
  ): Promise<AgentTestImportResult> {
    return this.request<AgentTestImportResult>(
      "/api/v1/agent-tests/import",
      this.jsonInit("POST", { format, content, dry_run: dryRun }),
    );
  }

  async agentTestImportTemplate(): Promise<string> {
    const response = await this.send("/api/v1/agent-tests/import/template.csv", {
      headers: { Accept: "text/csv" },
    });
    return response.text();
  }

  async startAgentTestRun(caseIds: string[] | null, minScore: number): Promise<AgentTestRun> {
    return this.request<AgentTestRun>(
      "/api/v1/agent-tests/runs",
      this.jsonInit("POST", { case_ids: caseIds, min_score: minScore }),
    );
  }

  async listAgentTestRuns(limit = 20): Promise<AgentTestRun[]> {
    const data = await this.request<{ runs: AgentTestRun[] }>(
      `/api/v1/agent-tests/runs?limit=${limit}`,
    );
    return data.runs;
  }

  async getAgentTestRun(runId: string): Promise<AgentTestRun> {
    return this.request<AgentTestRun>(`/api/v1/agent-tests/runs/${encodeURIComponent(runId)}`);
  }

  async listAgentTestResults(runId: string, failedOnly = false): Promise<AgentTestResult[]> {
    const data = await this.request<{ results: AgentTestResult[] }>(
      `/api/v1/agent-tests/runs/${encodeURIComponent(runId)}/results?failed_only=${failedOnly}`,
    );
    return data.results;
  }

  async agentTestRunExport(runId: string): Promise<string> {
    const response = await this.send(
      `/api/v1/agent-tests/runs/${encodeURIComponent(runId)}/export.csv`,
      { headers: { Accept: "text/csv" } },
    );
    return response.text();
  }

  /* ------------------------------------------ connection health probe */

  async probeLlmConnection(id: string): Promise<LLMProbeResult> {
    return this.request<LLMProbeResult>(
      `/api/v1/connections/llm/${encodeURIComponent(id)}/probe`,
      { method: "POST" },
    );
  }

  /* ----------------------------------------- emergency scripts (policy) */

  async getEmergencyScripts(): Promise<EmergencyScript[]> {
    const data = await this.request<{ scripts: EmergencyScript[] }>(
      "/api/v1/policy/emergency-scripts",
    );
    return data.scripts;
  }

  /* ---------------------------------------------- policy check (policy) */

  async checkPolicy(body: PolicyCheckRequest): Promise<PolicyCheckResult> {
    return this.request<PolicyCheckResult>(
      "/api/v1/policy/check",
      this.jsonInit("POST", body),
    );
  }

  /* ------------------------------------------- PII verification (compliance) */

  async getPiiVerification(limit = 50): Promise<PiiVerificationResult> {
    return this.request<PiiVerificationResult>(
      `/api/v1/compliance/pii-verification?limit=${limit}`,
    );
  }

  /* ================================================================ */
  /* Console overhaul 2026-09-03 (contracts/http/console.v1.openapi.yaml) */
  /* ================================================================ */

  /** Call archive: full-text plus attribute filters, newest first. */
  async searchCalls(params: CallSearchParams = {}): Promise<CallSearchResult> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      query.set(key, String(value));
    }
    const suffix = query.toString();
    return this.request<CallSearchResult>(
      `/api/v1/calls/search${suffix ? `?${suffix}` : ""}`,
    );
  }

  /** Current window plus the equally long window before it. */
  async getAnalyticsKpiTrend(period = "7d"): Promise<AnalyticsKpiTrend> {
    return this.request<AnalyticsKpiTrend>(
      `/api/v1/analytics/kpis?period=${encodeURIComponent(period)}`,
    );
  }

  /** Operator call control (listen_in, takeover, release, mute_ai) -> NATS. */
  async controlCall(
    callId: string,
    command: ControlCommand,
    operatorId: string,
  ): Promise<ControlResult> {
    return this.request<ControlResult>(
      `/api/v1/calls/${encodeURIComponent(callId)}/control`,
      this.jsonInit("POST", { command, operator_id: operatorId }),
    );
  }

  /** Per-turn feedback; rating "flag" also opens a knowledge gap. */
  async submitTurnFeedback(
    callId: string,
    turnSeq: number,
    rating: TurnFeedbackRating,
    comment?: string,
  ): Promise<{ status: string }> {
    return this.request(
      `/api/v1/calls/${encodeURIComponent(callId)}/turns/${turnSeq}/feedback`,
      this.jsonInit("POST", { rating, comment: comment ?? null }),
    );
  }

  async updateCallback(
    callbackId: string,
    body: CallbackUpdate,
  ): Promise<Pick<CallbackRequest, "callback_id" | "status" | "attempted_at" | "completed_at" | "notes">> {
    return this.request(
      `/api/v1/callbacks/${encodeURIComponent(callbackId)}`,
      this.jsonInit("PUT", body),
    );
  }

  async createCallback(body: CallbackCreateInput): Promise<CallbackRequest> {
    return this.request<CallbackRequest>(
      "/api/v1/callbacks",
      this.jsonInit("POST", body),
    );
  }

  /** Role-gated (callback PII reader); every read is audited server-side. */
  async getCallbackIntake(callbackId: string): Promise<CallbackIntakeDetail> {
    return this.request<CallbackIntakeDetail>(
      `/api/v1/callbacks/${encodeURIComponent(callbackId)}/intake`,
    );
  }

  async getHealthDetailed(): Promise<HealthDetail> {
    return this.request<HealthDetail>("/api/v1/health/detailed");
  }

  async getSyncStatus(): Promise<SyncStatus> {
    const data = await this.request<{ sync_status: SyncStatus }>(
      "/api/v1/connections/sync-status",
    );
    return data.sync_status;
  }

  async getIonosConfig(): Promise<IonosConfig | null> {
    const data = await this.request<{ ionos: IonosConfig | null }>(
      "/api/v1/connections/ionos",
    );
    return data.ionos;
  }

  async testIonos(): Promise<{ status: string; latency_ms?: number | null; error?: string | null }> {
    return this.request("/api/v1/connections/ionos/test", { method: "POST" });
  }

  async getAnrufregeln(): Promise<AnrufregelnResponse> {
    return this.request<AnrufregelnResponse>("/api/v1/anrufregeln");
  }

  async saveAnrufregeln(config: AnrufregelnConfig, version: number): Promise<AnrufregelnResponse> {
    return this.request<AnrufregelnResponse>("/api/v1/anrufregeln", this.jsonInit("PUT", { config, version }));
  }

  async getAgentSettings(): Promise<AgentSettings> {
    const data = await this.request<{ settings: AgentSettings }>(
      "/api/v1/settings",
    );
    return data.settings;
  }

  async updateAgentSettings(body: AgentSettingsUpdate): Promise<AgentSettings> {
    const data = await this.request<{ settings: AgentSettings }>(
      "/api/v1/settings",
      this.jsonInit("PUT", body),
    );
    return data.settings;
  }

  async listVoices(): Promise<Voice[]> {
    const data = await this.request<{ voices: Voice[] }>("/api/v1/settings/voices");
    return data.voices;
  }

  async listConfigVersions(area: ConfigArea, limit = 20): Promise<ConfigVersionList> {
    return this.request<ConfigVersionList>(
      `/api/v1/config-versions/${encodeURIComponent(area)}?limit=${limit}`,
    );
  }

  async getConfigVersion(area: ConfigArea, version: number): Promise<ConfigVersionDetail> {
    return this.request<ConfigVersionDetail>(
      `/api/v1/config-versions/${encodeURIComponent(area)}/${version}`,
    );
  }

  async revertConfigVersion(
    area: ConfigArea,
    version: number,
  ): Promise<{ reverted_to: number; new_version: number }> {
    return this.request(
      `/api/v1/config-versions/${encodeURIComponent(area)}/${version}/revert`,
      { method: "POST" },
    );
  }

  async getKnowledgeDocuments(): Promise<KnowledgeDocuments> {
    return this.request<KnowledgeDocuments>("/api/v1/knowledge/documents");
  }

  async searchKnowledge(question: string, topK = 5): Promise<KnowledgeSearchResult> {
    return this.request<KnowledgeSearchResult>(
      "/api/v1/knowledge/search",
      this.jsonInit("POST", { question, top_k: topK }),
    );
  }

  async getCostEstimates(period = "7d"): Promise<CostEstimate> {
    return this.request<CostEstimate>(
      `/api/v1/telemetry/costs?period=${encodeURIComponent(period)}`,
    );
  }

  /** DSGVO erasure for one caller hash; irreversible, confirm in the UI. */
  async requestErasure(aniHash: string): Promise<ErasureResult> {
    return this.request<ErasureResult>(
      "/api/v1/erasure/request",
      this.jsonInit("POST", { ani_hash: aniHash }),
    );
  }

  /** Help assistant: questions about the console, nothing persisted. */
  async assistantChat(body: AssistantChatRequest): Promise<AssistantChatResponse> {
    return this.request<AssistantChatResponse>(
      "/api/v1/assistant/chat",
      this.jsonInit("POST", body),
    );
  }
}
